import { Info, Play } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { api, type PromQueryData } from '../../api';
import { DataTable, Section, type Column } from '../../components/blocks';
import { DataError, OpsReasonBar } from '../../components/DataError';
import { LineChart, formatValue, type ChartSeries } from '../../components/LineChart';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { TimeRangePicker } from '../../components/TimeRangePicker';
import { defaultStep, MAX_QUERY_CHARS, namesBusinessObjects, rangeFromParams, rangeToParams, resolveRange, type TimeRange } from '../../lib/opsQuery';
import { useApi } from '../../lib/useApi';
import { eppLatencyQuery } from './RegistriesPage';

const EXAMPLES = [
  { id: 'http5xx', query: 'sum by (service) (rate(registrar_http_requests_total{status_class="5xx"}[5m])) / sum by (service) (rate(registrar_http_requests_total[5m]))' },
  { id: 'eppP95', query: eppLatencyQuery() },
  { id: 'eppCommands', query: 'sum by (operation) (rate(registrar_epp_commands_total[5m]))' },
  { id: 'workflows', query: 'sum by (state) (registrar_workflows_in_state)' },
  { id: 'outbox', query: 'sum(registrar_outbox_pending_events)' },
  { id: 'ledger', query: 'max(registrar_ledger_imbalance_entries)' },
] as const;

function seriesLabel(metric: Record<string, string>): string {
  const { __name__: name, ...rest } = metric;
  const labels = Object.entries(rest)
    .map(([k, v]) => `${k}="${v}"`)
    .join(', ');
  return `${name ?? ''}${labels ? `{${labels}}` : name ? '' : '{}'}`;
}

function toSeries(data: PromQueryData): ChartSeries[] {
  if (data.resultType !== 'matrix') return [];
  return data.result.map((s, i) => ({
    key: `${i}:${seriesLabel(s.metric)}`,
    label: seriesLabel(s.metric),
    points: s.values.map(([t, v]) => ({ t, v: v === 'NaN' ? null : Number(v) })),
  }));
}

const seriesCell = (label: string) => <span className="font-mono text-xs break-all">{label}</span>;

function InstantTable({ data }: { data: PromQueryData }) {
  const { t } = useTranslation('ops');
  if (data.resultType === 'scalar' || data.resultType === 'string') {
    return (
      <p className="flex items-baseline gap-2 px-5 pb-5">
        <strong className="tabular text-3xl font-semibold">{data.result[1]}</strong> <span className="text-sm text-muted-foreground">{data.resultType}</span>
      </p>
    );
  }
  let rows: { label: string; value: string }[] = [];
  if (data.resultType === 'vector') rows = data.result.map((r) => ({ label: seriesLabel(r.metric), value: r.value[1] }));
  else if (data.resultType === 'matrix') rows = data.result.map((r) => ({ label: seriesLabel(r.metric), value: r.values[r.values.length - 1]?.[1] ?? '—' }));
  const columns: Column<{ label: string; value: string }>[] = [
    { key: 'series', header: t('metrics.col.series'), cell: (r) => seriesCell(r.label) },
    { key: 'value', header: t('metrics.col.value'), numeric: true, cell: (r) => (r.value !== '—' && Number.isFinite(Number(r.value)) ? formatValue(Number(r.value)) : r.value) },
  ];
  return <DataTable columns={columns} rows={rows} rowKey={(r) => r.label} empty={<EmptyState title={t('metrics.emptyResult')} />} />;
}

function RangeTable({ series }: { series: ChartSeries[] }) {
  const { t } = useTranslation('ops');
  const stats = series.map((s) => {
    const vals = s.points.map((p) => p.v).filter((v): v is number => v !== null);
    return { key: s.key, label: s.label, vals };
  });
  const fmt = (v: number | undefined) => (v === undefined ? '—' : formatValue(v));
  const columns: Column<(typeof stats)[number]>[] = [
    { key: 'series', header: t('metrics.col.series'), cell: (r) => seriesCell(r.label) },
    { key: 'min', header: t('metrics.col.min'), numeric: true, cell: (r) => fmt(r.vals.length ? Math.min(...r.vals) : undefined) },
    { key: 'max', header: t('metrics.col.max'), numeric: true, cell: (r) => fmt(r.vals.length ? Math.max(...r.vals) : undefined) },
    { key: 'last', header: t('metrics.col.last'), numeric: true, cell: (r) => fmt(r.vals[r.vals.length - 1]) },
  ];
  return (
    <details className="group border-t">
      <summary className="cursor-pointer px-5 py-3 text-sm font-medium text-primary select-none hover:underline">{t('metrics.tableView', { count: series.length })}</summary>
      <DataTable columns={columns} rows={stats} rowKey={(r) => r.key} />
    </details>
  );
}

/** `/ops/metrics`: PromQL instant and range queries through `/ops/v1/metrics/*` (spec §98, §99). */
export function MetricsPage() {
  const { t } = useTranslation('ops');
  useDocumentTitle(t('metrics.title'));
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const mode = params.get('mode') === 'instant' ? 'instant' : 'range';
  const range = rangeFromParams(params, '1h');
  const stepParam = params.get('step');
  const [draft, setDraft] = useState(q);
  const [stepDraft, setStepDraft] = useState(stepParam ?? '');
  const [nonce, setNonce] = useState(0);
  const id = useId();
  const rangeKey = params.toString();

  const result = useApi(
    async (signal) => {
      if (!q) return null;
      if (mode === 'instant') return api.queryMetrics({ query: q, ...(range.kind === 'custom' ? { time: range.end } : {}) }, { signal });
      const { start, end } = resolveRange(range);
      const step = stepParam ? Number(stepParam) : defaultStep(start, end);
      return api.queryMetricsRange({ query: q, start: start.toISOString(), end: end.toISOString(), step }, { signal });
    },
    [rangeKey, nonce],
  );

  const apply = (next: { q?: string; mode?: string; range?: TimeRange; step?: string }) => {
    const p = new URLSearchParams(params);
    if (next.q !== undefined) p.set('q', next.q);
    if (next.mode !== undefined) p.set('mode', next.mode);
    if (next.range) rangeToParams(next.range, p);
    if (next.step !== undefined) {
      if (next.step) p.set('step', next.step);
      else p.delete('step');
    }
    setParams(p);
    setNonce((n) => n + 1);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (draft.trim()) apply({ q: draft.trim(), step: stepDraft.trim() });
  };

  const series = result.status === 'success' && result.data ? toSeries(result.data) : [];

  return (
    <>
      <PageHeader title={t('metrics.title')} subtitle={t('metrics.subtitle')} />
      <div className="space-y-6">
        <OpsReasonBar />
        <Section>
          <form className="space-y-4" onSubmit={submit}>
            <div className="grid gap-1.5">
              <label htmlFor={`${id}-q`} className="text-xs font-medium text-muted-foreground">
                {t('metrics.promql')}
              </label>
              <Textarea
                id={`${id}-q`}
                className="min-h-20 font-mono text-sm"
                rows={3}
                value={draft}
                maxLength={MAX_QUERY_CHARS}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(e);
                }}
                spellCheck={false}
                placeholder="sum by (service) (rate(registrar_http_requests_total[5m]))"
              />
              <p className="text-xs text-muted-foreground">{t('shared.runHint')}</p>
              {namesBusinessObjects(draft) ? (
                <p className="flex items-center gap-1.5 text-xs text-warning">
                  <Info className="size-3.5" aria-hidden="true" /> {t('shared.reasonHint')}
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <ToggleGroup type="single" variant="outline" size="sm" value={mode} onValueChange={(v) => v && apply({ mode: v })} aria-label={t('metrics.mode')}>
                <ToggleGroupItem value="range">{t('metrics.range')}</ToggleGroupItem>
                <ToggleGroupItem value="instant">{t('metrics.instant')}</ToggleGroupItem>
              </ToggleGroup>
              {mode === 'range' ? (
                <div className="flex items-center gap-2">
                  <label htmlFor={`${id}-step`} className="text-sm text-muted-foreground">
                    {t('metrics.step')}
                  </label>
                  <Input id={`${id}-step`} type="number" min={1} value={stepDraft} onChange={(e) => setStepDraft(e.target.value)} placeholder={t('metrics.stepAuto')} className="h-8 w-24" />
                </div>
              ) : null}
              <Button type="submit" size="sm" disabled={!draft.trim()}>
                <Play /> {t('shared.run')}
              </Button>
            </div>
            <TimeRangePicker key={rangeKey} value={range} onChange={(r) => apply({ range: r })} />
            {mode === 'instant' ? <p className="text-xs text-muted-foreground">{t('metrics.instantHint')}</p> : null}
          </form>
          <div className="mt-5 border-t pt-4">
            <p className="mb-2 text-xs font-medium text-muted-foreground">{t('metrics.examplesTitle')}</p>
            <div className="flex flex-wrap gap-2">
              {EXAMPLES.map((ex) => (
                <Button
                  key={ex.id}
                  type="button"
                  variant="secondary"
                  size="xs"
                  className="h-7 rounded-full px-3"
                  title={ex.query}
                  onClick={() => {
                    setDraft(ex.query);
                    apply({ q: ex.query });
                  }}
                >
                  {t(`metrics.example.${ex.id}`)}
                </Button>
              ))}
            </div>
          </div>
        </Section>

        <Section title={q ? t('metrics.resultTitle') : undefined} flush>
          {!q ? (
            <div className="px-5 pb-5">
              <EmptyState title={t('metrics.emptyTitle')}>{t('metrics.emptyBody')}</EmptyState>
            </div>
          ) : null}
          {q && result.status === 'loading' ? (
            <div className="px-5">
              <Loading label={t('metrics.querying')} />
            </div>
          ) : null}
          {q && result.status === 'error' ? (
            <div className="px-5 pb-5">
              <DataError error={result.error} onRetry={() => setNonce((n) => n + 1)} title={t('metrics.failed')} />
            </div>
          ) : null}
          {result.status === 'success' && result.data ? (
            mode === 'range' && series.length ? (
              <>
                <div className="px-5 pb-4">
                  <LineChart series={series} title={q} />
                </div>
                <RangeTable series={series} />
              </>
            ) : (
              <InstantTable data={result.data} />
            )
          ) : null}
        </Section>
      </div>
    </>
  );
}
